import * as FullStory from '@fullstory/browser'
import {FULLSTORY_ORG} from '../../config/envConfig'
import {ROUTES} from '@/routes/routeConfig'

/**
 * Section mapping for the application
 * Maps route paths to human-readable section names
 * Names match FullStory page tracking for consistency
 */
export const SECTION_MAPPING: Record<string, string> = {
  '/': 'Home',
  '/team': 'Team Detail',
  '/program': 'Program Detail',
  '/se-metrics': 'SE Metrics',
  '/ai-productivity': 'AI Productivity',
  '/faqs': 'FAQs',
  '/metrics-settings': 'Metrics Settings',
  '/login': 'Authentication - Login',
  '/login/callback': 'Authentication - Callback',
  '/logout': 'Authentication - Logout',
}

/**
 * Get section name from pathname
 * Handles dynamic routes like /team/:id and /program/:id
 * Falls back to the centralized ROUTES config for routes not in SECTION_MAPPING
 */
export const getSectionName = (pathname: string): string => {
  // Exact match from legacy mapping
  if (SECTION_MAPPING[pathname]) {
    return SECTION_MAPPING[pathname]
  }

  // Handle dynamic routes
  // Check for nested team route first: /program/:programId/team/:teamId
  if (pathname.includes('/team/') && pathname.startsWith('/program/')) {
    return 'Team Detail'
  }
  if (pathname.startsWith('/team/')) {
    return 'Team Detail'
  }
  if (pathname.startsWith('/program/')) {
    return 'Program Detail'
  }

  // Look up label from centralized route config (single source of truth)
  const route = ROUTES.find(r => {
    if (r.path === '/') return pathname === '/'
    return pathname === r.path || pathname.startsWith(`${r.path}/`)
  })
  if (route?.label) return route.label

  // Default fallback
  return pathname || 'Unknown Section'
}

/**
 * Check if FullStory is initialized and ready
 */
export const isFullStoryReady = (): boolean => {
  // Check if FullStory org ID is valid (not empty or placeholder)
  const hasValidOrgId = !!FULLSTORY_ORG && FULLSTORY_ORG.trim() !== ''

  // Also check if FullStory is actually initialized (window.FS exists)
  const isInitialized = typeof window !== 'undefined' && !!window.FS

  return hasValidOrgId && isInitialized
}

/**
 * Track a page view event in FullStory
 * @param pathname - The current pathname
 * @param sectionName - Human-readable section name
 */
export const trackPageView = (pathname: string, sectionName: string): void => {
  if (!isFullStoryReady()) {
    console.warn('[FullStory] Not ready - skipping page view tracking', {
      pathname,
      sectionName,
      orgId: FULLSTORY_ORG,
      windowFSExists: typeof window !== 'undefined' && !!window.FS,
      hasValidOrgId: !!FULLSTORY_ORG && FULLSTORY_ORG.trim() !== '',
    })
    return
  }

  try {
    // Set page vars for custom properties
    FullStory.setVars('page', {
      pageName_str: sectionName,
      path_str: pathname,
    })

    // Track custom event for additional analytics
    FullStory.event('Page View', {
      section_str: sectionName,
      pathname_str: pathname,
      timestamp_int: Date.now(),
    })

    console.log(`[FullStory] Page tracked: ${sectionName} (${pathname})`)
  } catch (error) {
    console.error('[FullStory] Error tracking page:', error)
  }
}

/**
 * Track a section visit event (for sidebar navigation)
 * @param sectionId - The section ID from sidebar
 * @param sectionLabel - The section label
 */
export const trackSectionVisit = (sectionId: string, sectionLabel: string): void => {
  if (!isFullStoryReady()) {
    return
  }

  try {
    FullStory.event('Section Visit', {
      sectionId_str: sectionId,
      sectionLabel_str: sectionLabel,
      timestamp_int: Date.now(),
    })

    console.log(`[FullStory] Section visit tracked: ${sectionLabel} (${sectionId})`)
  } catch (error) {
    console.error('[FullStory] Error tracking section visit:', error)
  }
}

/**
 * Track a custom event in FullStory
 * @param eventName - Name of the event
 * @param properties - Event properties (must follow FullStory naming conventions)
 */
export const trackCustomEvent = (eventName: string, properties?: Record<string, any>): void => {
  if (!isFullStoryReady()) {
    return
  }

  try {
    FullStory.event(eventName, properties || {})
    console.log(`[FullStory] Custom event tracked: ${eventName}`, properties)
  } catch (error) {
    console.error(`[FullStory] Error tracking custom event ${eventName}:`, error)
  }
}
